import classNames from 'classnames';
import { useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { useUiMessages } from '../../messages';
import type { CalendarSelection } from '../Calendar';
import { DEFAULT_MONTH_LABELS } from '../Calendar/dates';
import { DatePicker } from '../DatePicker';
import { DropdownMenu } from '../DropdownMenu';
import { Icon } from '../Icon';
import { Typography } from '../Typography';
import { UtilityButton } from '../UtilityButton';
import type { UtilityButtonSize } from '../UtilityButton';

import styles from './DateField.module.css';

export type DateFieldSize = 'sm' | 'md';

const SHORT_MONTH_LEN = 3;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_PAD_LEN = 2;
/**
 * `DropdownMenu`'s own default (320px) caps a scrollable *list* of menu items — `DatePicker`'s
 * single-month card is a fixed, self-contained layout (nav + field row + 6-week grid + Cancel/
 * Apply footer) that is always taller than that, so the default clipped the footer clean off.
 * This is comfortably above the card's real height with headroom for the widest French month
 * names; `overflow-y: auto` on the panel only engages if it is somehow still exceeded.
 */
const PANEL_MAX_HEIGHT = 640;

export interface DateFieldProps {
  /** ISO `yyyy-MM-dd`, or `''` for no value. */
  value: string;
  /** Fires only when a pick is applied (see `DatePicker`'s Cancel/Apply footer) — never per draft tick. */
  onChange: (value: string) => void;
  label?: ReactNode;
  hideLabel?: boolean;
  hintText?: ReactNode;
  errorMessage?: ReactNode;
  destructive?: boolean;
  size?: DateFieldSize;
  placeholder?: string;
  /** Accessible name for the clear button (rendered only while `value !== ''`). */
  clearLabel?: string;
  minDate?: Date;
  maxDate?: Date;
  required?: boolean;
  disabled?: boolean;
  /** Accessible name when no visible `label` is provided. */
  ariaLabel?: string;
  id?: string;
  className?: string;
}

/** `yyyy-MM-dd` → local-time `Date` (never UTC — must match `Calendar/dates.ts`'s own local-time convention). */
function parseIsoDate(value: string): Date | null {
  const match = ISO_DATE.exec(value);
  if (match?.[1] == null || match[2] == null || match[3] == null) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** Local-time `Date` → `yyyy-MM-dd` (mirrors `parseIsoDate`; never `toISOString`, which reads UTC). */
function toIsoDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(ISO_PAD_LEN, '0');
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

const UTILITY_SIZE: Record<DateFieldSize, UtilityButtonSize> = { sm: 'xs', md: 'sm' };

/**
 * A closed field that opens the shared `DatePicker` on demand — an `Input`-styled trigger
 * (button, not a native text field: the value is chosen, never typed) anchored to a
 * `DropdownMenu` `role="dialog"` panel. Picking a day only updates `DatePicker`'s own internal
 * draft; `onChange` fires exclusively on Apply, and Cancel/Escape/outside-click close without
 * committing, leaving the field's prior value untouched. Wraps `DatePicker` unchanged — no
 * footer-hiding, no instant-commit mode.
 *
 * @interim Composes existing primitives (`DatePicker`, `DropdownMenu`); replaceable by the UI Kit.
 */
export function DateField({
  value,
  onChange,
  label,
  hideLabel = false,
  hintText,
  errorMessage,
  destructive = false,
  size = 'md',
  placeholder,
  clearLabel,
  minDate,
  maxDate,
  required,
  disabled = false,
  ariaLabel,
  id,
  className,
}: DateFieldProps) {
  const messages = useUiMessages();
  const resolvedPlaceholder = placeholder ?? messages.select?.placeholder ?? 'Select…';
  const resolvedClearLabel = clearLabel ?? 'Clear date';
  const resolvedMonthLabels = messages.calendar?.months ?? DEFAULT_MONTH_LABELS;

  const autoId = useId();
  const rootId = id ?? autoId;
  const labelId = `${rootId}-label`;
  const messageId = `${rootId}-message`;
  const hasError = destructive || errorMessage != null;
  const message = errorMessage ?? hintText;

  const [open, setOpen] = useState(false);
  // Bumped only when the picker opens, never on close — a fresh `DatePicker` instance (and so a
  // draft re-seeded from the field's current `value`) each time it opens, but the *same* instance
  // stays mounted through a mobile-sheet close animation instead of blanking out mid-slide.
  const [openKey, setOpenKey] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedDate = parseIsoDate(value);
  const hasValue = selectedDate != null;

  const formatValue = (date: Date) =>
    `${resolvedMonthLabels[date.getMonth()]?.slice(0, SHORT_MONTH_LEN) ?? ''} ${String(date.getDate())}, ${String(date.getFullYear())}`;

  const close = () => {
    setOpen(false);
  };

  const handleApply = (selection: CalendarSelection) => {
    const picked = selection instanceof Date ? selection : null;
    onChange(picked == null ? '' : toIsoDate(picked));
    setOpen(false);
  };

  return (
    <div className={classNames(styles.field, className)}>
      {label != null && (
        <span id={labelId} className={classNames(styles.label, hideLabel && styles.srOnly)}>
          <Typography as="span" variant="text-sm" weight="medium">
            {label}
          </Typography>
          {required && (
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          )}
        </span>
      )}

      <div
        className={classNames(
          styles.box,
          styles[size],
          hasError && styles.error,
          disabled && styles.disabled,
        )}
      >
        <button
          ref={triggerRef}
          type="button"
          id={rootId}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? `${rootId}-panel` : undefined}
          aria-labelledby={label != null ? labelId : undefined}
          aria-label={label == null ? ariaLabel : undefined}
          aria-describedby={message != null ? messageId : undefined}
          disabled={disabled}
          className={styles.trigger}
          onClick={() => {
            setOpen((prev) => {
              const next = !prev;
              if (next) setOpenKey((k) => k + 1);
              return next;
            });
          }}
        >
          <Icon name="calendar" size={20} className={classNames(styles.icon)} aria-hidden />
          <span className={classNames(styles.value, !hasValue && styles.placeholder)}>
            {selectedDate != null ? formatValue(selectedDate) : resolvedPlaceholder}
          </span>
        </button>

        {hasValue && (
          <UtilityButton
            size={UTILITY_SIZE[size]}
            hierarchy="tertiary"
            icon={<Icon name="x-close" size={16} />}
            label={resolvedClearLabel}
            disabled={disabled}
            className={styles.clear}
            onClick={() => {
              onChange('');
            }}
          />
        )}
      </div>

      <DropdownMenu
        anchorRef={triggerRef}
        open={open}
        onClose={close}
        role="dialog"
        matchWidth={false}
        maxHeight={PANEL_MAX_HEIGHT}
        id={`${rootId}-panel`}
        aria-labelledby={label != null ? labelId : undefined}
        aria-label={label == null ? ariaLabel : undefined}
      >
        <DatePicker
          key={openKey}
          mode="single"
          defaultValue={selectedDate}
          onApply={handleApply}
          onCancel={close}
          {...(minDate != null ? { minDate } : {})}
          {...(maxDate != null ? { maxDate } : {})}
        />
      </DropdownMenu>

      {message != null && (
        <Typography
          as="p"
          variant="text-sm"
          id={messageId}
          className={classNames(hasError ? styles.errorText : styles.hint)}
        >
          {message}
        </Typography>
      )}
    </div>
  );
}
